import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Badge,
  ProgressBar,
  Button,
  Table,
  Spinner
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";

const API_BASE_URL = "http://13.205.22.175";
const REVIEW_QUEUE_URL = "/api/admin/tdsReviewqueue";
const API_TOKEN = "7fa5d115dbbf825a0a4abb2b2a7f37de";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Authorization: `Bearer ${API_TOKEN}`,
    "Content-Type": "application/json",
  },
});

const HomeLoan = () => {
  const [homeLoanData, setHomeLoanData] = useState(null);
  const [loading, setLoading] = useState(true); // Add local loading state
  const [error, setError] = useState(null);

  // 1. Get User Context from LocalStorage
  const storedUser = JSON.parse(localStorage.getItem("user_data") || "{}");
  const tdsUser = JSON.parse(localStorage.getItem("tdsUser") || "{}");

  const staffId =
    tdsUser.staffId ||
    tdsUser.id ||
    tdsUser.employeeId ||
    storedUser.staffId ||
    storedUser.id;
  const userId = storedUser.user_id || storedUser.id;
  const financialYear =
    storedUser.financialYear || tdsUser.financialYear || "2025-2026";
  const authToken =
    localStorage.getItem("token") || storedUser.token || storedUser.access_token;
  const requestHeaders = {
    "Content-Type": "application/json",
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  };

  const fetchReviewQueueData = async () => {
    if (!staffId || !userId) {
      setError(new Error("Missing user or staff information."));
      toast.warning("Please login again or select an employee");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api.post(
        REVIEW_QUEUE_URL,
        {
          userId,
          staffId: Number(staffId),
          financialYear,
        },
        {
          headers: requestHeaders,
        }
      );

      const body = response?.data;
      if (body && body.code === 200 && body.data) {
        setHomeLoanData(body.data);
      } else {
        throw new Error(body?.message || "Invalid API response");
      }
    } catch (fetchError) {
      console.error("Error fetching review queue data:", fetchError);
      setError(fetchError);
      toast.error("Failed to load home loan data");
    } finally {
      setLoading(false);
    }
  };

  // 3. Fetch Data on Mount
  useEffect(() => {
    fetchReviewQueueData();
  }, []);

  const handleRefresh = async () => {
    await fetchReviewQueueData();
  };

  // 4. Loading State
  if (loading) { // Check local loading state instead
    return (
      <div className="d-flex justify-content-center align-items-center vh-100" style={{ backgroundColor: "#0f0f1a", color: "white" }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading Home Loan Data...</span>
      </div>
    );
  }

  // 5. Error State
  if (error) {
    return (
      <div className="text-center text-danger p-5" style={{ backgroundColor: "#0f0f1a", minHeight: "100vh" }}>
        <h4>Error loading data</h4>
        <p>{error.message || "Unknown error occurred"}</p>
        <Button 
          variant="primary" 
          onClick={handleRefresh}
          className="mt-3"
          disabled={loading}
        >
          Retry
        </Button>
      </div>
    );
  }

  // 6. Check if we have data
  if (!homeLoanData) {
    return (
      <div className="text-center text-warning p-5" style={{ backgroundColor: "#0f0f1a", minHeight: "100vh" }}>
        <h4>No Data Available</h4>
        <p>Home loan data could not be loaded or is not available for this employee.</p>
        <Button 
          variant="primary" 
          onClick={() => window.location.reload()}
          className="mt-3"
        >
          Try Again
        </Button>
      </div>
    );
  }

  // 7. Data Extraction from API Response
  const employeeInfo = homeLoanData.employeeInfo || {};
  const sections = homeLoanData.sections || {};
  const overallStatus = homeLoanData.reviewStatus || homeLoanData.overallStatus || {};
  
  // Extract home loan data - check multiple possible structures
  const homeLoanSection = sections.homeLoan || sections.housingLoan || {};
  const documentsSection = homeLoanSection.documents || sections.documents || homeLoanData.documentsSection || {};
  
  // Extract fields from homeLoanSection
  const fields = homeLoanSection.fields || {};
  
  // Helper function to safely extract values
  const getFieldValue = (fieldName, defaultValue = "") => {
    const field = fields[fieldName];
    if (field && field.value !== undefined && field.value !== null && field.value !== "") {
      return field.value;
    }
    // If not in fields, check directly in homeLoanSection
    if (homeLoanSection[fieldName] !== undefined && homeLoanSection[fieldName] !== null && homeLoanSection[fieldName] !== "") {
      return homeLoanSection[fieldName];
    }
    return defaultValue;
  };

  // Extract loan details from API
  const lenderName = getFieldValue("lenderName");
  const lenderPan = getFieldValue("lenderPan");
  const propertyAddress = getFieldValue("propertyAddress");
  const ownershipType = getFieldValue("ownershipType");
  const loanAccountNo = getFieldValue("loanAccountNo");
  const loanAmount = parseInt(getFieldValue("loanAmount", "0")) || 0;
  const interestRate = parseFloat(getFieldValue("interestRate", "0")) || 0;
  const tenure = parseInt(getFieldValue("tenure", "0")) || 0;
  const startDate = getFieldValue("startDate");
  const loanPurpose = getFieldValue("loanPurpose");
  const finalMaturityDate = getFieldValue("finalMaturityDate");
  
  // Extract interest calculations
  const interestPaid = parseInt(getFieldValue("interestPaid", "0")) || 0;
  const maxDeduction = ownershipType === "Self-occupied" ? 200000 : 0;
  const eligibleDeduction = Math.min(interestPaid, maxDeduction);
  
  // Extract document information - check API structure for documents
  const getDocumentStatus = (docType) => {
    if (!documentsSection || typeof documentsSection !== 'object') return false;
    
    // Check multiple possible structures
    const doc = documentsSection[docType] || 
                documentsSection[docType.toLowerCase()] ||
                documentsSection[docType + 'Document'] ||
                (documentsSection.documents && documentsSection.documents[docType]);
    
    return doc ? (doc.url || doc.status === "uploaded" || doc === true) : false;
  };

  const hasLenderCertificate = getDocumentStatus("lenderCertificate") || 
                              getDocumentStatus("homeLoanCertificate") ||
                              getDocumentStatus("form12BB");
  
  const hasLoanStatement = getDocumentStatus("loanStatement") || 
                          getDocumentStatus("accountStatement");
  
  const hasAgreementCopy = getDocumentStatus("agreementCopy") || 
                          getDocumentStatus("loanAgreement");

  // Get document URL if available
  const getDocumentUrl = (docType) => {
    if (!documentsSection || typeof documentsSection !== 'object') return null;
    
    const doc = documentsSection[docType] || 
                documentsSection[docType.toLowerCase()] ||
                documentsSection[docType + 'Document'] ||
                (documentsSection.documents && documentsSection.documents[docType]);
    
    return doc ? (doc.url || doc.path || doc.fileUrl) : null;
  };

  const lenderCertificateUrl = getDocumentUrl("lenderCertificate") || 
                              getDocumentUrl("homeLoanCertificate") ||
                              getDocumentUrl("form12BB");

  // Extract OCR analysis data - check API structure
  const ocrAnalysis = homeLoanSection.ocrAnalysis || homeLoanData.ocrAnalysis || documentsSection.ocrAnalysis || {};
  const ocrStatus = ocrAnalysis.status || "Not Available";
  const ocrConfidence = ocrAnalysis.confidence || ocrAnalysis.confidenceScore || 0;
  
  // Extract remarks and audit trail
  const remarks = getFieldValue("remarks") || 
                  homeLoanData.remarks || 
                  homeLoanSection.reviewStatus?.remarks ||
                  overallStatus.remarks ||
                  "No remarks available";
  
  const lastActionBy = homeLoanData.lastActionBy || overallStatus.lastActionBy || "";
  const lastActionDate = homeLoanData.lastActionDate || overallStatus.lastActionDate || "";

  // Extract validation data
  const isPanValid = lenderPan && lenderPan.length === 10;
  const isWithinLimit = interestPaid <= maxDeduction;
  const documentStatus = hasLenderCertificate ? "Verified" : "Pending";

  // Calculate tenure completed (if we have start date)
  const calculateTenureCompleted = () => {
    if (!startDate) return 0;
    try {
      const start = new Date(startDate);
      const now = new Date();
      const diffYears = now.getFullYear() - start.getFullYear();
      return Math.min(diffYears, tenure);
    } catch {
      return 0;
    }
  };

  const tenureCompleted = calculateTenureCompleted();

  // 8. Action Handlers
  const executeReviewAction = async (action, additionalData = {}) => {
    if (!staffId || !userId) {
      toast.warning("Unable to perform action: missing user or staff data.");
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(
        REVIEW_QUEUE_URL,
        {
          userId,
          staffId: Number(staffId),
          financialYear,
          action,
          section: "homeLoan",
          ...additionalData,
        },
        {
          headers: requestHeaders,
        }
      );

      const body = response?.data;
      if (body && body.code === 200) {
        toast.success(`Action ${action} completed successfully.`);
        if (body.data) {
          setHomeLoanData(body.data);
        } else {
          await fetchReviewQueueData();
        }
      } else {
        throw new Error(body?.message || "Action failed");
      }
    } catch (actionError) {
      console.error(`Error executing ${action}:`, actionError);
      toast.error(`Failed to ${action.replace("_", " ")}`);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveDeduction = async () => {
    await executeReviewAction("approve_section", { deductionAmount: eligibleDeduction });
  };

  const handleReject = async () => {
    await executeReviewAction("reject_section", {
      remarks: "Document verification pending",
    });
  };

  const handleRequestMoreDocs = async () => {
    await executeReviewAction("request_section", {
      remarks: "Please upload the lender certificate to complete verification.",
      requestedDocuments: ["lenderCertificate"],
    });
  };

  const handleAddComment = async () => {
    await executeReviewAction("comment_section", {
      remarks: "Reviewed by tax approver. Please verify lender certificate and interest details.",
    });
  };

  const handleUploadDocument = async () => {
    await executeReviewAction("upload_document", {
      documentType: "lenderCertificate",
      status: "uploaded",
    });
  };

  return (
    <div 
      className="p-4" 
      style={{ 
        backgroundColor: "#0f0f1a", 
        minHeight: '100vh',
        color: "#e0e0e0",
        fontFamily: 'Arial, sans-serif'
      }}
    >
      {/* Main Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="text-white mb-2" style={{ fontWeight: 600, color: "#6f42c1" }}>
            Home Loan Interest Deduction
          </h2>
          <p className="text-muted mb-3">
            Source and use of home loan interest deduction under Section 24(b)
          </p>
          
          <div className="d-flex align-items-center gap-3">
            <Badge bg="primary" className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
              Employee: {employeeInfo.employeeName || employeeInfo.fullName || "N/A"}
            </Badge>
            <Badge bg="info" className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
              PAN: {employeeInfo.employeePAN || employeeInfo.panNumber || "N/A"}
            </Badge>
            <Badge bg={ownershipType ? "success" : "secondary"} className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
              {ownershipType || "Type not specified"}
            </Badge>
            <Badge bg="secondary" className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
              FY {homeLoanData.financialYear || storedUser.financialYear || "2025-26"}
            </Badge>
          </div>
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
          <Button 
            variant="outline-light" 
            size="sm"
            onClick={handleRefresh}
            className="me-2"
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : "Refresh"}
          </Button>
          <Button 
            variant="outline-info" 
            size="sm"
            onClick={() => toast.info("Export feature coming soon")}
            disabled={loading}
          >
            Export
          </Button>
        </Col>
      </Row>

      <Row className="g-4">
        {/* Left Column - Loan Details & Calculations */}
        <Col lg={8}>
          {/* Loan Details Card */}
          <Card className="mb-4 border-0" style={{ backgroundColor: "#1a1a2e" }}>
            <Card.Header className="bg-gradient-purple text-white border-0 d-flex justify-content-between align-items-center">
              <h5 className="mb-0" style={{ fontWeight: 600 }}>Loan Details</h5>
              <Badge bg={isPanValid ? "success" : lenderPan ? "danger" : "secondary"}>
                {isPanValid ? "PAN Valid" : lenderPan ? "PAN Invalid" : "PAN Missing"}
              </Badge>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={6}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Lender Name</label>
                    <div className={`p-2 rounded ${lenderName ? "text-white bg-dark" : "text-muted bg-secondary"}`}>
                      {lenderName || "Not provided"}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Lender PAN</label>
                    <div className={`p-2 rounded ${lenderPan ? "text-white fw-bold bg-dark" : "text-muted bg-secondary"}`}>
                      {lenderPan || "Not provided"}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Loan Account Number</label>
                    <div className={`p-2 rounded ${loanAccountNo ? "text-white bg-dark" : "text-muted bg-secondary"}`}>
                      {loanAccountNo || "Not provided"}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Loan Amount</label>
                    <div className={`p-2 rounded ${loanAmount > 0 ? "text-white fw-bold bg-dark" : "text-muted bg-secondary"}`}>
                      {loanAmount > 0 ? `₹${loanAmount.toLocaleString('en-IN')}` : "Not provided"}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Interest Rate</label>
                    <div className={`p-2 rounded ${interestRate > 0 ? "text-white bg-dark" : "text-muted bg-secondary"}`}>
                      {interestRate > 0 ? `${interestRate}% p.a.` : "Not provided"}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Tenure</label>
                    <div className={`p-2 rounded ${tenure > 0 ? "text-white bg-dark" : "text-muted bg-secondary"}`}>
                      {tenure > 0 ? `${tenure} years` : "Not provided"}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Start Date</label>
                    <div className={`p-2 rounded ${startDate ? "text-white bg-dark" : "text-muted bg-secondary"}`}>
                      {startDate || "Not provided"}
                    </div>
                  </div>
                </Col>
                <Col md={12}>
                  <div className="mb-3">
                    <label className="text-muted d-block mb-1">Property Address</label>
                    <div className={`p-2 rounded ${propertyAddress ? "text-white bg-dark" : "text-muted bg-secondary"}`} style={{ minHeight: '60px' }}>
                      {propertyAddress || "Not provided"}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Interest Deduction Calculation */}
          <Card className="mb-4 border-0" style={{ backgroundColor: "#1a1a2e" }}>
            <Card.Header className="bg-gradient-purple text-white border-0">
              <h5 className="mb-0" style={{ fontWeight: 600 }}>Interest Deduction Computation</h5>
            </Card.Header>
            <Card.Body>
              <Row className="mb-4">
                <Col md={6}>
                  <div className="d-flex justify-content-between p-3 border-bottom border-secondary">
                    <span>Interest Paid (Declared)</span>
                    <strong className="text-white">₹{interestPaid.toLocaleString('en-IN')}</strong>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex justify-content-between p-3 border-bottom border-secondary">
                    <span>Max Limit ({ownershipType || "N/A"})</span>
                    <strong className="text-white">₹{maxDeduction.toLocaleString('en-IN')}</strong>
                  </div>
                </Col>
              </Row>

              {/* Calculation Result */}
              <div className="text-center mb-4">
                <div className="p-4 rounded" style={{ 
                  background: "linear-gradient(135deg, rgba(106, 17, 203, 0.2), rgba(37, 117, 252, 0.2))",
                  border: "2px solid #6f42c1"
                }}>
                  <h6 className="text-muted mb-3">Eligible Deduction u/s 24(b)</h6>
                  <h1 className="display-5 fw-bold mb-3 text-white">₹{eligibleDeduction.toLocaleString('en-IN')}</h1>
                  <p className="text-muted mb-0">
                    Lower of Interest Paid (₹{interestPaid.toLocaleString('en-IN')}) 
                    or Max Limit (₹{maxDeduction.toLocaleString('en-IN')})
                  </p>
                </div>
              </div>

              {/* Utilization Criteria */}
              <div className="mt-4">
                <h6 className="text-white mb-3" style={{ fontWeight: 600 }}>Utilization Criteria</h6>
                <Row className="g-3">
                  <Col md={4}>
                    <div className="p-3 text-center bg-dark rounded">
                      <div className="text-muted small mb-1">Loan Purpose</div>
                      <div className="text-white">{loanPurpose || "Not specified"}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-3 text-center bg-dark rounded">
                      <div className="text-muted small mb-1">Tenure Completed</div>
                      <div className="text-white">{tenureCompleted} years</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-3 text-center bg-dark rounded">
                      <div className="text-muted small mb-1">Final Maturity Date</div>
                      <div className="text-white">{finalMaturityDate || "Not specified"}</div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>

          {/* Discussions & Remarks Section */}
          <Card className="mb-4 border-0" style={{ backgroundColor: "#1a1a2e" }}>
            <Card.Header className="bg-gradient-purple text-white border-0">
              <h5 className="mb-0" style={{ fontWeight: 600 }}>Discussions & Remarks</h5>
            </Card.Header>
            <Card.Body>
              <div className="p-3 bg-dark rounded mb-3">
                <div className="text-muted small mb-1">Remarks/Notes</div>
                <div className="text-white">
                  {remarks}
                </div>
              </div>
              
              <Row className="g-3">
                <Col md={6}>
                  <div className="p-3 bg-dark rounded">
                    <div className="text-muted small mb-1">Last Action By</div>
                    <div className="text-white">{lastActionBy || "N/A"}</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="p-3 bg-dark rounded">
                    <div className="text-muted small mb-1">Last Action Date</div>
                    <div className="text-white">{lastActionDate || "N/A"}</div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Right Column - Documents & Actions */}
        <Col lg={4}>
          {/* Document Guidelines */}
          <Card className="mb-4 border-0" style={{ backgroundColor: "#1a1a2e" }}>
            <Card.Header className="bg-gradient-purple text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0" style={{ fontWeight: 600 }}>Document Guidelines</h5>
              <Badge bg={hasLenderCertificate ? "success" : "warning"}>
                {hasLenderCertificate ? "Complete" : "Pending"}
              </Badge>
            </Card.Header>
            <Card.Body>
              <div className="mb-4">
                <ProgressBar 
                  now={hasLenderCertificate ? 100 : hasLoanStatement ? 50 : 0} 
                  label={hasLenderCertificate ? "Complete" : "In Progress"}
                  variant={hasLenderCertificate ? "success" : "warning"}
                  style={{ height: "12px", borderRadius: "6px" }}
                />
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center p-3 bg-dark rounded mb-2">
                  <div>
                    <span className="text-white">Lender Certificate</span>
                    <div className="text-muted small">Form 12BB/Home Loan Certificate</div>
                  </div>
                  <Badge bg={hasLenderCertificate ? "success" : "danger"}>
                    {hasLenderCertificate ? "Uploaded" : "Missing"}
                  </Badge>
                </div>
                
                <div className="d-flex justify-content-between align-items-center p-3 bg-dark rounded mb-2">
                  <div>
                    <span className="text-white">Loan Statement PDF</span>
                    <div className="text-muted small">Loan Account Statement</div>
                  </div>
                  <Badge bg={hasLoanStatement ? "success" : "warning"}>
                    {hasLoanStatement ? "Uploaded" : "Optional"}
                  </Badge>
                </div>
                
                <div className="d-flex justify-content-between align-items-center p-3 bg-dark rounded">
                  <div>
                    <span className="text-white">Agreement Copy</span>
                    <div className="text-muted small">Loan Agreement Copy</div>
                  </div>
                  <Badge bg={hasAgreementCopy ? "success" : "secondary"}>
                    {hasAgreementCopy ? "Uploaded" : "Not Required"}
                  </Badge>
                </div>
              </div>
              
              {hasLenderCertificate && lenderCertificateUrl && (
                <Button 
                  variant="outline-info" 
                  size="sm" 
                  className="w-100"
                  onClick={() => window.open(lenderCertificateUrl, '_blank')}
                >
                  View Lender Certificate
                </Button>
              )}
            </Card.Body>
          </Card>

          {/* OCR Analysis Section */}
          <Card className="mb-4 border-0" style={{ backgroundColor: "#1a1a2e" }}>
            <Card.Header className="bg-gradient-purple text-white">
              <h5 className="mb-0" style={{ fontWeight: 600 }}>OCR Analysis</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-white">Document Status</span>
                  <Badge bg={ocrStatus === "Verified" ? "success" : "warning"}>
                    {ocrStatus}
                  </Badge>
                </div>
                
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-white">Confidence Score</span>
                  <Badge bg={ocrConfidence > 90 ? "success" : "warning"}>
                    {ocrConfidence > 0 ? `${ocrConfidence}%` : "N/A"}
                  </Badge>
                </div>
                
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-white">Extracted Data</span>
                  <Badge bg={lenderName || lenderPan || loanAmount > 0 ? "info" : "secondary"}>
                    {lenderName || lenderPan || loanAmount > 0 ? "Available" : "N/A"}
                  </Badge>
                </div>
                
                <div className="mt-3 p-3 bg-dark rounded">
                  <div className="text-muted small mb-1">Extracted Information</div>
                  <div className="text-white small">
                    {lenderName ? `• Lender: ${lenderName}` : ""}
                    {lenderPan ? `\n• PAN: ${lenderPan}` : ""}
                    {loanAmount > 0 ? `\n• Amount: ₹${loanAmount.toLocaleString('en-IN')}` : ""}
                    {interestRate > 0 ? `\n• Interest Rate: ${interestRate}%` : ""}
                    {!lenderName && !lenderPan && !loanAmount && !interestRate ? "No data extracted" : ""}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Validation Status */}
          <Card className="mb-4 border-0" style={{ backgroundColor: "#1a1a2e" }}>
            <Card.Header className="bg-gradient-purple text-white">
              <h5 className="mb-0" style={{ fontWeight: 600 }}>Validation Status</h5>
            </Card.Header>
            <Card.Body>
              <Table borderless className="text-white mb-0">
                <tbody>
                  <tr>
                    <td className="py-2 border-bottom border-secondary">
                      <span className="d-flex align-items-center">
                        <span className="me-2">Lender PAN</span>
                        {lenderPan ? (isPanValid ? "✓" : "✗") : "-"}
                      </span>
                    </td>
                    <td className="py-2 text-end border-bottom border-secondary">
                      {lenderPan ? (
                        isPanValid ? (
                          <Badge bg="success">Valid</Badge>
                        ) : (
                          <Badge bg="danger">Invalid</Badge>
                        )
                      ) : (
                        <Badge bg="secondary">Missing</Badge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 border-bottom border-secondary">
                      <span className="d-flex align-items-center">
                        <span className="me-2">Limit Check</span>
                        {interestPaid > 0 ? (isWithinLimit ? "✓" : "⚠") : "-"}
                      </span>
                    </td>
                    <td className="py-2 text-end border-bottom border-secondary">
                      {interestPaid > 0 ? (
                        isWithinLimit ? (
                          <Badge bg="success">Within Limit</Badge>
                        ) : (
                          <Badge bg="warning" text="dark">Exceeds</Badge>
                        )
                      ) : (
                        <Badge bg="secondary">Not Declared</Badge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 border-bottom border-secondary">
                      <span className="d-flex align-items-center">
                        <span className="me-2">Document Status</span>
                        {hasLenderCertificate ? "✓" : "✗"}
                      </span>
                    </td>
                    <td className="py-2 text-end border-bottom border-secondary">
                      {hasLenderCertificate ? (
                        <Badge bg="success">Verified</Badge>
                      ) : (
                        <Badge bg="danger">Pending</Badge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2">
                      <span className="d-flex align-items-center">
                        <span className="me-2">Overall Status</span>
                        {lenderPan && interestPaid > 0 && hasLenderCertificate ? (isPanValid && isWithinLimit ? "✓" : "⚠") : "-"}
                      </span>
                    </td>
                    <td className="py-2 text-end">
                      {lenderPan && interestPaid > 0 && hasLenderCertificate ? (
                        isPanValid && isWithinLimit ? (
                          <Badge bg="success">Approved</Badge>
                        ) : (
                          <Badge bg="warning" text="dark">Review Needed</Badge>
                        )
                      ) : (
                        <Badge bg="secondary">Incomplete</Badge>
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Action Buttons */}
          <Card className="mb-4 border-0" style={{ backgroundColor: "#1a1a2e" }}>
            <Card.Header className="bg-gradient-purple text-white">
              <h5 className="mb-0" style={{ fontWeight: 600 }}>Actions</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-3">
                <Button 
                  variant="success" 
                  size="lg"
                  onClick={handleApproveDeduction}
                  disabled={!isPanValid || !isWithinLimit || !hasLenderCertificate || eligibleDeduction <= 0 || loading}
                  className="py-3"
                >
                  <div className="d-flex flex-column">
                    <span>Approve Deduction</span>
                    <small className="opacity-75">₹{eligibleDeduction.toLocaleString('en-IN')}</small>
                  </div>
                </Button>
                
                <Button 
                  variant="danger" 
                  size="lg"
                  onClick={handleReject}
                  className="py-3"
                  disabled={loading}
                >
                  Reject Application
                </Button>
                
                <Button 
                  variant="outline-info" 
                  size="lg"
                  onClick={handleRequestMoreDocs}
                  disabled={hasLenderCertificate || loading}
                  className="py-3"
                >
                  Request Additional Documents
                </Button>

                <Button
                  variant="outline-warning"
                  size="lg"
                  onClick={handleAddComment}
                  disabled={loading}
                  className="py-3"
                >
                  Add Review Comment
                </Button>

                <Button
                  variant="outline-success"
                  size="lg"
                  onClick={handleUploadDocument}
                  disabled={loading || hasLenderCertificate}
                  className="py-3"
                >
                  Mark Lender Certificate Uploaded
                </Button>

                <Button 
                  variant="outline-light" 
                  size="lg"
                  onClick={handleRefresh}
                  className="py-3"
                  disabled={loading}
                >
                  Refresh Data
                </Button>
              </div>
              
              <div className="mt-4 p-3 bg-dark rounded">
                <small className="text-muted">
                  <strong>Note:</strong> Home loan interest deduction under Section 24(b) is limited to ₹2,00,000 per financial year for self-occupied property. Document verification is mandatory for approval.
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default HomeLoan;
